/**
 * Client 360 — FR-B7. One client, everything the firm knows about them.
 * Redrawn on the record shell in Wave 15B (C14 §10.6 item 3, ADR-0033;
 * T-6.14): one header — back link, the name, the key facts, **Edit** as the
 * one primary action and **Convert** under *More* — and the client's parts as
 * tabs: Overview (details and history), People, Cases, Documents, Chat and
 * Ledger. The tab is in the address (`?tab=`), so a link can open one.
 *
 * **Every panel is filled.** Cases and due dates arrived with Wave 4A, the
 * ledger with 4C, documents and chat with 4B (Wave 5 close §3.4, closed in
 * Wave 6C). Each tab reads its own part when it is opened, because each is
 * its own permission (`./ClientPanels.tsx`).
 *
 * The rule the old placeholders were written for outlived them and still
 * holds: **nothing here invents a number.** A plausible-but-wrong ledger
 * balance on a client's page is worse than an empty panel and it will be
 * believed (CLAUDE.md §1a). So every figure below is the server's, every
 * permission is the server's answer, and a part that cannot be read says which
 * one it is rather than rendering a confident `₹0.00`.
 *
 * **Archive and restore** live under *More* (C27 item 1, UAT report #1), each
 * gated by `client:can_delete` as the service gates both routes. Archiving
 * asks first, and the service refuses it while work is open — the dialog then
 * lists what is open (*2 open cases and 1 unpaid invoice*) so the firm knows
 * what to finish. An archived client shows *Restore client* instead, refused
 * when an active client of the firm has since taken its PAN.
 */

import { Alert, Button, ConfirmDialog, type DropdownMenuItem } from '@fiveox/ui'
import { Archive, Pencil, Repeat, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'

import { RecordPage, type RecordFact } from '../../components/page'
import { CLIENT, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import {
  type Client360 as Client360Payload,
  PAN_TAKEN,
  WORK_OPEN,
  clients,
  conflictReason,
  describeKind,
  openWorkSentence,
  panDisplay,
} from '../../lib/api/clients'
import { ClientCasePanels } from './CasePanels'
import { ClientChatPanel, ClientDocumentsPanel, ClientLedgerPanel } from './ClientPanels'
import { ConvertDialog } from './ConvertDialog'
import { EditClientDialog } from './EditClientDialog'
import { OverviewTab } from './OverviewTab'
import { PeopleTab } from './PeopleTab'

export const CLIENT_TABS = ['overview', 'people', 'cases', 'documents', 'chat', 'ledger'] as const

/** The facts a reader needs before anything else. A PAN or GSTIN that is not
 *  held is left out rather than shown as a dash — the Overview tab says so. */
export function clientFacts(client: Client360Payload['client']): RecordFact[] {
  const facts: RecordFact[] = [{ label: 'Kind', value: describeKind(client) }]
  if (client.display_name) facts.push({ label: 'Known as', value: client.display_name })
  if (client.pan) {
    facts.push({ label: 'PAN', value: <span className="font-mono">{panDisplay(client.pan)}</span> })
  }
  if (client.gstin) {
    facts.push({ label: 'GSTIN', value: <span className="font-mono">{client.gstin}</span> })
  }
  if (client.converted_at) facts.push({ label: 'Converted', value: 'Yes' })
  if (client.status === 'archived') facts.push({ label: 'Status', value: 'Archived' })
  return facts
}

/**
 * The refusal to archive, in the firm's words. `work_open` names what is open
 * from the service's counts; anything else is the service's own message.
 */
export function archiveRefusal(err: unknown): string {
  if (conflictReason(err) === WORK_OPEN && err instanceof ApiError) {
    const open = openWorkSentence(err.details.open)
    if (open) {
      return `This client still has ${open}. Close or settle them first, then archive the client.`
    }
  }
  return err instanceof ApiError ? err.message : 'The client could not be archived.'
}

/** The refusal to restore. `pan_taken` is the one a firm can act on. */
export function restoreRefusal(err: unknown): string {
  if (conflictReason(err) === PAN_TAKEN) {
    return 'Another active client of your firm now holds this PAN, so this client can’t be restored. Change that client’s PAN, or archive it, then restore this one.'
  }
  return err instanceof ApiError ? err.message : 'The client could not be restored.'
}

export function Client360() {
  const { clientId = '' } = useParams()
  const { can } = useEvaluation()
  const [params, setParams] = useSearchParams()

  const [view, setView] = useState<Client360Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ text: string; tone: 'success' | 'info' } | null>(null)
  const [converting, setConverting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setView((await clients.view(clientId)).data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'This client could not be loaded.')
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const back = { to: '/clients', label: 'All clients' }
  const wanted = params.get('tab')
  const tab = (CLIENT_TABS as readonly string[]).includes(wanted ?? '') ? wanted! : 'overview'
  const chooseTab = (id: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        if (id === 'overview') next.delete('tab')
        else next.set('tab', id)
        return next
      },
      { replace: true },
    )

  if (!view) {
    return <RecordPage back={back} title="Client" loading error={error} />
  }

  const { client, people, history, invitations } = view
  // w7a — ACCEPTANCE §3.1: Edit did not exist, and with it the only way to
  // record a client's email address was the API. Both acts are
  // `client:can_update`: an edit and a conversion change the same record, and
  // the service checks the same verb on both routes.
  const mayUpdate = can(CLIENT, 'can_update')
  // C27 item 1 — archive and restore are both `client:can_delete`, the verb the
  // service checks on both routes.
  const mayDelete = can(CLIENT, 'can_delete')
  const archived = client.status === 'archived'

  const archive = async () => {
    setBusy(true)
    setArchiveError(null)
    try {
      await clients.archive(client.id)
      setArchiving(false)
      setNotice({
        text: `${client.legal_name} is archived. Their people can no longer reach this client in the portal, and you can restore them from Clients → Archived.`,
        tone: 'success',
      })
      await load()
    } catch (err) {
      // The dialog stays open with the reason: it is the one place the firm
      // is looking, and *what is still open* is what they need to act on.
      setArchiveError(archiveRefusal(err))
    } finally {
      setBusy(false)
    }
  }

  const restore = async () => {
    setBusy(true)
    setRestoreError(null)
    setNotice(null)
    try {
      await clients.restore(client.id)
      setNotice({ text: `${client.legal_name} is active again.`, tone: 'success' })
      await load()
    } catch (err) {
      setRestoreError(restoreRefusal(err))
    } finally {
      setBusy(false)
    }
  }

  const more: DropdownMenuItem[] = []
  if (mayUpdate) {
    more.push({
      id: 'convert',
      label: 'Convert',
      icon: <Repeat size={14} aria-hidden />,
      description: 'Change whether this is an individual or a business.',
      onSelect: () => setConverting(true),
    })
  }
  if (mayDelete) {
    more.push(
      archived
        ? {
            id: 'restore',
            label: 'Restore client',
            icon: <RotateCcw size={14} aria-hidden />,
            description: 'Put this client back on your client list.',
            disabled: busy,
            separatorBefore: more.length > 0,
            onSelect: () => void restore(),
          }
        : {
            id: 'archive',
            label: 'Archive client',
            icon: <Archive size={14} aria-hidden />,
            description: 'Take this client off your list. Nothing is deleted.',
            tone: 'danger',
            separatorBefore: more.length > 0,
            onSelect: () => {
              setArchiveError(null)
              setArchiving(true)
            },
          },
    )
  }

  return (
    <>
      <RecordPage
        back={back}
        title={client.legal_name}
        facts={clientFacts(client)}
        primary={
          mayUpdate ? (
            <Button onClick={() => setEditing(true)}>
              <Pencil size={16} aria-hidden /> Edit
            </Button>
          ) : undefined
        }
        more={more.length > 0 ? more : undefined}
        error={error}
        tab={tab}
        onTabChange={chooseTab}
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            content: <OverviewTab client={client} history={history} />,
          },
          {
            id: 'people',
            label: 'People',
            badge: people.length || undefined,
            content: (
              <PeopleTab
                client={client}
                people={people}
                invitations={invitations}
                onChanged={() => void load()}
                onNotice={setNotice}
              />
            ),
          },
          // w4a — the Cases and Due-dates parts the Wave 3 close assigned here
          // (§6 item 5). One read serves both; see `./CasePanels.tsx`.
          { id: 'cases', label: 'Cases', content: <ClientCasePanels clientId={client.id} /> },
          // w6c — the last three seams on this screen, filled. Each is its own
          // read because each is its own permission — see `./ClientPanels.tsx`.
          // w28e — for a firm that has moved onto ProDoc, ProDoc's package
          // (ADR-0055 §6); `client.prodoc` is I-5's, and the panel asks the
          // client read itself when this read does not carry it.
          {
            id: 'documents',
            label: 'Documents',
            content: <ClientDocumentsPanel clientId={client.id} prodoc={client.prodoc} />,
          },
          { id: 'chat', label: 'Chat', content: <ClientChatPanel clientId={client.id} /> },
          { id: 'ledger', label: 'Ledger', content: <ClientLedgerPanel clientId={client.id} /> },
        ]}
      >
        {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
        {restoreError && <Alert tone="error">{restoreError}</Alert>}
      </RecordPage>

      <ConfirmDialog
        open={archiving}
        onClose={() => setArchiving(false)}
        title={`Archive ${client.legal_name}?`}
        confirmLabel="Archive client"
        cancelLabel="Keep the client"
        tone="danger"
        busy={busy}
        busyLabel="Archiving…"
        error={archiveError}
        onConfirm={() => void archive()}
      >
        <p className="text-muted-foreground text-sm">
          They leave your client list, and the people linked to them lose access to this client in
          your portal. Nothing is deleted — their cases, invoices, documents and history stay, their
          PAN is freed for a new client, and you can restore them later.
        </p>
      </ConfirmDialog>

      {editing && (
        <EditClientDialog
          client={client}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            setNotice({ text: 'Saved.', tone: 'success' })
            void load()
          }}
          onReloaded={() => void load()}
        />
      )}
      {converting && (
        <ConvertDialog
          client={client}
          onClose={() => setConverting(false)}
          onConverted={() => {
            setConverting(false)
            void load()
          }}
        />
      )}
    </>
  )
}
